import React, { useCallback, useEffect, useMemo, useRef } from 'react'
import { Box } from '@mui/material'
import { useLocalMedia, useRoomConnection } from '@whereby.com/browser-sdk/react'
import type {
  LocalParticipant as WherebyLocalParticipant,
  RemoteParticipant as WherebyRemoteParticipant,
  RoomConnection as WherebyRoomConnection
} from '@whereby.com/browser-sdk/react'
import { useTranslation } from '../../../../shared/hooks/useIntractionTranslate'
import { Button as DesignSystemButton } from '../../../../interaxDesignSystemComponents'
import { TESTIDS } from '../../../../config/testids'
import { CallHeader } from './CallHeader'
import { DeviceMenu } from './DeviceMenu'
import { PermissionsWarningBanner } from './PermissionsWarningBanner'
import {
  getWherebyParticipantDisplayName,
  isWherebyBotParticipant
} from '../../../../../../pure-js/libs/isWherebyBotParticipant'
import { registerCallSurfaceCapture, type CallSurfaceCaptureRegistration } from '../../helpers/permissionHelpers'
import { useWherebyCallLifecycle } from './useWherebyCallLifecycle'
import { useWherebyCallControls } from './useWherebyCallControls'
import { useIsCallSilentAfterReconnect } from './wherebyCallReconnect'
import { WherebyVideoArea } from './WherebyVideoArea'
import { WherebyRoomConnectionStatus } from '../../../../../../pure-js/enums/Whereby'

type RoomConnectionState = Omit<WherebyRoomConnection, 'events'>

/** One connected participant; `userId` is the Whereby `externalId` (which we set to our app user id). */
type WherebyCallParticipant = { userId: string | null; displayName: string }

interface WherebyCallSurfaceProps {
  roomUrl: string
  displayName: string
  teamName: string
  /** Local user's app id — sent as Whereby externalId so other clients can identify this participant. */
  userId?: string | null
  onLeaveCall?: () => void
  onAssistantPresenceChange?: (isPresent: boolean) => void
  /** Emitted whenever the connected participant set changes (local + remote, with userIds). */
  onParticipantsChange?: (participants: WherebyCallParticipant[]) => void
  onMinimize?: () => void
  /** Starts the call again from scratch; offered only when it came back from a drop with nobody's sound or picture. */
  onReconnect?: () => void
  compact?: boolean
  showHangupButton?: boolean
  /** Offers sharing a screen; the calls a whole sprint holds together, where someone presents. */
  canShareScreen?: boolean
}

const EMPTY_PARTICIPANTS: WherebyRemoteParticipant[] = []
const ASSISTANT_NAME_FRAGMENT = 'intraction assistant'

const CONNECTION_LABEL_KEYS: Partial<Record<WherebyRoomConnectionStatus, string>> = {
  [WherebyRoomConnectionStatus.CONNECTED]: 'whereby.status.connected',
  [WherebyRoomConnectionStatus.RECONNECTING]: 'whereby.status.reconnecting',
  [WherebyRoomConnectionStatus.DISCONNECTED]: 'whereby.status.failed',
  [WherebyRoomConnectionStatus.KICKED]: 'whereby.status.failed',
  [WherebyRoomConnectionStatus.KNOCK_REJECTED]: 'whereby.status.failed'
}

export const WherebyCallSurface: React.FC<WherebyCallSurfaceProps> = ({
  roomUrl,
  displayName,
  teamName,
  userId,
  onLeaveCall,
  onAssistantPresenceChange,
  onParticipantsChange,
  onMinimize,
  onReconnect,
  compact = false,
  showHangupButton = true,
  canShareScreen = false
}) => {
  const { t: translate } = useTranslation()
  // Callback refs keep parent rerenders from restarting notification effects.
  const onAssistantPresenceChangeRef = useRef(onAssistantPresenceChange)
  const onParticipantsChangeRef = useRef(onParticipantsChange)
  onAssistantPresenceChangeRef.current = onAssistantPresenceChange
  onParticipantsChangeRef.current = onParticipantsChange

  const captureRegistrationRef = useRef<CallSurfaceCaptureRegistration | null>(null)
  // Register before useLocalMedia so its effect assigns camera requests to this call generation.
  useEffect(() => {
    const captureRegistration = registerCallSurfaceCapture()
    captureRegistrationRef.current = captureRegistration
    return () => {
      captureRegistration.release()
      if (captureRegistrationRef.current === captureRegistration) captureRegistrationRef.current = null
    }
  }, [])
  const localMedia = useLocalMedia({ audio: true, video: true })
  const localStreamRef = useRef(localMedia.state.localStream)
  localStreamRef.current = localMedia.state.localStream
  const hasLiveMicrophoneTrack = Boolean(
    localMedia.state.localStream?.getAudioTracks().some((track) => track.readyState === 'live')
  )
  // Wait for outgoing audio before joining; confirmed media errors still join for permission recovery.
  const canJoinRoom =
    hasLiveMicrophoneTrack || Boolean(localMedia.state.startError || localMedia.state.microphoneDeviceError)
  const stopLocalMedia = useCallback(() => {
    captureRegistrationRef.current?.release()
    localStreamRef.current?.getTracks().forEach((track) => track.stop())
  }, [])
  const stopCameraCapture = useCallback(() => captureRegistrationRef.current?.stopCamera(), [])
  // The external id lets other clients map each Whereby participant to an application user.
  const roomConnection = useRoomConnection(roomUrl, {
    localMedia,
    displayName: displayName || undefined,
    externalId: userId || undefined
  })
  const { state, actions } = roomConnection
  const roomState = state as RoomConnectionState

  // Keep the empty participant list stable while the SDK initializes.
  const remoteParticipants = (roomState.remoteParticipants ?? EMPTY_PARTICIPANTS) as WherebyRemoteParticipant[]
  const localParticipant = roomState.localParticipant as WherebyLocalParticipant | undefined
  const connectionStatus = roomState.connectionStatus as WherebyRoomConnectionStatus
  const { requestLeave } = useWherebyCallLifecycle({
    displayName,
    canJoinRoom,
    connectionStatus,
    joinRoom: actions.joinRoom,
    leaveRoom: actions.leaveRoom,
    stopLocalMedia,
    setDisplayName: actions.setDisplayName,
    onLeaveComplete: onLeaveCall
  })
  const controls = useWherebyCallControls({
    actions,
    localMedia,
    isCameraEnabled: roomState.isCameraEnabled,
    isMicrophoneEnabled: roomState.isMicrophoneEnabled,
    requestLeave,
    showHangupButton,
    canShareScreen,
    isSharingScreen: roomState.localScreenshareStatus !== undefined,
    stopCameraCapture,
    translate
  })

  const participantList = useMemo(() => {
    const list: Array<{ id: string; userId: string | null; name: string }> = []
    if (localParticipant) {
      list.push({ id: localParticipant.id ?? 'local', userId: userId ?? null, name: displayName })
    }
    remoteParticipants.forEach((participant, index) => {
      const name = getWherebyParticipantDisplayName(participant, translate('whereby.participant.anonymous'))
      // Recorder and captioner bots do not count as connected people.
      if (isWherebyBotParticipant({ roleName: participant.roleName, displayName: name })) {
        return
      }
      list.push({
        id: participant.id ?? `remote-${index}`,
        userId: participant.externalId ?? null,
        name
      })
    })
    return list
  }, [displayName, userId, localParticipant, remoteParticipants, translate])

  const isAssistantPresent = participantList.some((participant) =>
    participant.name.toLowerCase().includes(ASSISTANT_NAME_FRAGMENT)
  )

  useEffect(() => {
    onAssistantPresenceChangeRef.current?.(isAssistantPresent)
  }, [isAssistantPresent])

  // Serialize the projected participant data so identity-only SDK rerenders do not notify consumers.
  const callParticipants = useMemo<WherebyCallParticipant[]>(
    () => participantList.map((participant) => ({ userId: participant.userId, displayName: participant.name })),
    [participantList]
  )
  const callParticipantsKey = useMemo(() => JSON.stringify(callParticipants), [callParticipants])
  useEffect(() => {
    onParticipantsChangeRef.current?.(callParticipants)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [callParticipantsKey])

  const connectionState = connectionStatus ?? WherebyRoomConnectionStatus.CONNECTING
  const connectionLabel = translate(CONNECTION_LABEL_KEYS[connectionState] ?? 'whereby.status.connecting')
  const isSilentAfterReconnect = useIsCallSilentAfterReconnect({ connectionStatus, remoteParticipants })
  const shouldOfferReconnect = isSilentAfterReconnect && Boolean(onReconnect)

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%', height: '100%', minHeight: 0 }}>
      <CallHeader
        teamName={teamName}
        connectionState={connectionState}
        connectionLabel={connectionLabel}
        onMinimize={onMinimize}
        minimizeTooltip={translate('gamer.gameCenter.minimizeCall')}
      />

      <PermissionsWarningBanner />

      {shouldOfferReconnect ? (
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <DesignSystemButton
            variant="primary"
            onClick={() => onReconnect?.()}
            data-testid={TESTIDS.WHEREBY_CALL_RECONNECT_BTN}
          >
            {translate('whereby.global.reconnect')}
          </DesignSystemButton>
        </Box>
      ) : null}

      <WherebyVideoArea
        compact={compact}
        displayName={displayName}
        localParticipant={localParticipant}
        remoteParticipants={remoteParticipants}
        screenshares={roomState.screenshares}
        anonymousName={translate('whereby.participant.anonymous')}
        showParticipants={controls.showParticipants}
        participantList={participantList}
        participantsTitle={translate('whereby.participants.title', { count: participantList.length })}
        toolbarItems={controls.toolbarItems}
        onSettingsOpen={controls.openDeviceMenu}
      />

      <DeviceMenu
        anchorEl={controls.deviceMenuAnchorEl}
        onClose={controls.closeDeviceMenu}
        microphoneDevices={(localMedia.state.microphoneDevices ?? []) as Array<{ deviceId: string; label: string }>}
        cameraDevices={(localMedia.state.cameraDevices ?? []) as Array<{ deviceId: string; label: string }>}
        speakerDevices={(localMedia.state.speakerDevices ?? []) as Array<{ deviceId: string; label: string }>}
        currentMicrophoneDeviceId={localMedia.state.currentMicrophoneDeviceId}
        currentCameraDeviceId={localMedia.state.currentCameraDeviceId}
        currentSpeakerDeviceId={localMedia.state.currentSpeakerDeviceId}
        onMicrophoneSelect={controls.selectMicrophone}
        onCameraSelect={controls.selectCamera}
        onSpeakerSelect={controls.selectSpeaker}
      />
    </Box>
  )
}
